// Main shell for the MyDeskey student dashboard.
// Provides: theme variables, header with switcher + credits, sidebar nav, sub-route content.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { signedOutPath } from '@/lib/loginEndpoint';
import { Routes, Route, useNavigate, Navigate, useLocation, useParams } from 'react-router-dom';
import api from './studentApi';
import { useApp } from '@/context';
import { getTheme, themeToCssVars, surfaceToAppTokens, FONT_SCALES, injectFontLink } from './themes';
import PlacedStickerBoard, { isCustomizableRoute } from './PlacedStickerBoard';
import {
  Wallet, Zap, ChevronDown, LogOut, Menu, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { confirmDialog } from '@/components/ConfirmDialog';

import BadgesDropdown from './BadgesDropdown';
import useHeartbeat from './useHeartbeat';
import { showBadgeToast, showTrophyToast } from './BadgeUnlockToast';
import StudentDeskPage from './StudentDeskPage';
import StudentSettingsPage from './StudentSettingsPage';
import StudentCreditsPage from './StudentCreditsPage';
import StudentHubPage from './StudentHubPage';
import StudentAnalyticsPage from './StudentAnalyticsPage';
import StudentBadgesPage from './StudentBadgesPage';
import StudentAssignmentsPage from './StudentAssignmentsPage';
import { AssignmentsHeaderPill } from './AssignmentsWidgets';
import MyDeskPage from './mydesk/MyDeskPage';
import MyDeskClassPage from './mydesk/MyDeskClassPage';
import LearnNowPage from './mydesk/LearnNowPage';
import TrophyClaimGate, { TrophyClaimHeaderPill } from './TrophyClaimGate';
import { StudentSidebar } from './StudentNav';
import ExamPrepHub from './ExamPrepHub';
import MyQuizzesPage from './MyQuizzesPage';
import CreateQuizPage from './CreateQuizPage';
import FormulaSheetPage from './FormulaSheetPage';
import PracticePyqPage from './PracticePyqPage';
import StudentQuizAttempt from '../StudentQuizAttempt';
import { notifyError } from '../../lib/errors';
import Loader from '../../components/Loader';

// Legacy class/topic routes — redirect to the rich MyDesk equivalent so the
// new LessonView is used everywhere. Kept as inline components so a single
// import covers both routes without polluting the page directory.
function LegacyClassRedirect() {
  const { classId } = useParams();
  return <Navigate to={`/student/mydesk/class/${classId}`} replace />;
}
function LegacyTopicRedirect() {
  const { topicId } = useParams();
  return <Navigate to={`/student/mydesk/lesson/${topicId}`} replace />;
}

// ─── Context ────────────────────────────────────────────────────────────────
const StudentCtx = createContext(null);
export const useStudent = () => useContext(StudentCtx);

// ─── Avatar menu — replaces the old bare avatar + standalone logout icon.
// Click the avatar to see name/email/a "Student" badge, with a Log out
// action gated behind a confirm dialog. ──────────────────────────────────────
function StudentAvatarMenu({ me, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const handleLogout = async () => {
    const ok = await confirmDialog({
      title: 'Log out?',
      message: 'You’ll need to sign in again to access MyDeskey.',
      confirmLabel: 'Log out',
      danger: true,
    });
    if (ok) onLogout();
  };

  const initial = (me?.full_name || 'U').slice(0, 1).toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="student-avatar-btn"
        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold transition-transform hover:scale-105"
        style={{ background: 'linear-gradient(135deg, var(--st-primary), var(--st-accent))' }}
        title={me?.full_name || 'Account'}>
        {initial}
      </button>
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-64 rounded-2xl shadow-xl border-2 overflow-hidden"
          style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)' }}
          data-testid="student-avatar-menu">
          <div className="p-4 flex items-center gap-3" style={{ background: 'var(--st-bg)' }}>
            <div className="w-11 h-11 rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, var(--st-primary), var(--st-accent))' }}>
              {initial}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold truncate">{me?.full_name || 'Student'}</div>
              <div className="text-[11px] opacity-60 truncate">{me?.email || ''}</div>
            </div>
          </div>
          <div className="px-4 pt-3 pb-1">
            <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-full text-white"
              style={{ background: 'var(--st-primary)' }}>
              Student
            </span>
          </div>
          <div className="p-2 pt-2">
            <button
              onClick={handleLogout}
              data-testid="student-logout-btn"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-red-500 hover:bg-red-500/10 transition-colors">
              <LogOut className="w-4 h-4" />
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shell ──────────────────────────────────────────────────────────────────
export default function StudentApp() {
  const nav = useNavigate();
  const { logout } = useApp();
  // Clears the app's signed-in user too, so Back can't reopen the desk afterwards.
  const signOut = useCallback(() => { logout(); nav(signedOutPath(), { replace: true }); }, [logout, nav]);
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [activeTid, setActiveTid] = useState(null);
  const [customStickers, setCustomStickers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badgeRefresh, setBadgeRefresh] = useState(0);

  // Heartbeat to track active minutes & award streak/time-based badges
  useHeartbeat({
    enabled: !!me,
    onUnlock: ({ badges, trophies }) => {
      badges.forEach(showBadgeToast);
      trophies.forEach(showTrophyToast);
      setBadgeRefresh(t => t + 1);
    },
  });

  const refreshMe = useCallback(async () => {
    try {
      const d = await api.get('/student/me');
      setMe(d.user);
      setMemberships(d.memberships || []);
      setActiveTid(d.active_tenant_id || null);
      if (d.needs_onboarding && location.pathname !== '/student/onboarding') {
        nav('/student/onboarding', { replace: true });
      }
      // Load custom stickers in parallel (non-blocking)
      api.get('/student/profile/stickers/custom').then(r => setCustomStickers(r.stickers || [])).catch(() => {});
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        signOut();
      } else {
        notifyError(e, 'Could not load profile');
      }
    } finally {
      setLoading(false);
    }
  }, [nav, location.pathname, signOut]);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  // Keep the header credits chip live: any page that spends or adds credits
  // (tutor chat, image generation, MagicDesk, top-ups, …) dispatches
  // 'desky:credits-changed' (see studentApi.notifyCreditsChanged) and the
  // whole profile — super_credits + membership credits_remaining — refetches.
  useEffect(() => {
    const onCreditsChanged = () => { refreshMe(); };
    window.addEventListener('desky:credits-changed', onCreditsChanged);
    return () => window.removeEventListener('desky:credits-changed', onCreditsChanged);
  }, [refreshMe]);

  const switchInstitution = useCallback(async (tid) => {
    try {
      const d = await api.post('/student/memberships/switch', { tenant_id: tid });
      localStorage.setItem('desky_token', d.token);
      setActiveTid(tid);
      setShowSwitcher(false);
      toast.success(`Switched to ${d.active_membership?.tenant_name || 'institution'}`);
      // Notify every "live count" component to refetch immediately so the
      // header pills (assignments, trophies, credits) update in the same
      // tick rather than waiting for their next polling interval.
      try { window.dispatchEvent(new CustomEvent('desky:tenant-switched', { detail: { tenant_id: tid } })); } catch { /* noop */ }
      // refresh me + send the student to the unified Desk so every block
      // refetches under the new tenant scope.
      await refreshMe();
      nav('/student/desk', { replace: true });
    } catch (e) {
      notifyError(e, 'Failed to switch');
    }
  }, [nav, refreshMe]);

  const profile = me?.student_profile || {};
  const themeId = profile.theme_id || 'deskey-classic';
  const theme = useMemo(() => getTheme(themeId), [themeId]);
  const fontScale = useMemo(() => {
    const sc = (FONT_SCALES.find(s => s.id === (profile.font_scale || 'md')) || FONT_SCALES[1]).multiplier;
    return sc;
  }, [profile.font_scale]);
  const cssVars = useMemo(
    () => ({
      ...themeToCssVars(theme, profile.accent_color, fontScale, profile.font_override),
      // Dark themes also remap the app-wide shadcn tokens (index.css is
      // light-only) -- otherwise headings and shared components stay
      // near-black on the dark background. Light themes keep the defaults.
      ...(theme.dark ? surfaceToAppTokens(theme) : {}),
    }),
    [theme, profile.accent_color, profile.font_override, fontScale]
  );
  // The theme only paints the shell div; <html>/<body> kept index.css's
  // white, which showed through on overscroll bounce and as a thin line at
  // the right edge. Paint them with the theme's own page background so the
  // bounce blends in (a bright primary band under a dark page clashed), and
  // restore the originals on theme change / leaving the student app.
  const pageBg = theme.bg;
  useEffect(() => {
    const html = document.documentElement;
    const prev = {
      htmlBg: html.style.backgroundColor, bodyBg: document.body.style.backgroundColor,
      scheme: html.style.colorScheme,
    };
    html.style.backgroundColor = pageBg;
    document.body.style.backgroundColor = pageBg;
    html.style.colorScheme = theme.dark ? 'dark' : 'light';
    return () => {
      html.style.backgroundColor = prev.htmlBg;
      document.body.style.backgroundColor = prev.bodyBg;
      html.style.colorScheme = prev.scheme;
    };
  }, [pageBg, theme.dark]);
  const activeMem = useMemo(() => memberships.find(m => m.tenant_id === activeTid), [memberships, activeTid]);

  // Inject Google Fonts <link> once
  useEffect(() => { injectFontLink(); }, []);

  const isImmersive = /\/student\/mydesk\/lesson\//.test(location.pathname);

  if (loading) {
    return (
      <Loader fullscreen label="Loading MyDeskey…" />
    );
  }

  // Profile failed to load (network / server error) — offer a retry instead of a blank page.
  if (!me) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-white">
        <div className="text-sm text-slate-600">Couldn't load your account</div>
        <button type="button" onClick={() => { setLoading(true); refreshMe(); }}
          className="h-10 text-sm font-medium text-white bg-[#1A7F37] hover:bg-[#166B2E] px-4 rounded-lg transition-colors">
          Try again
        </button>
      </div>
    );
  }

  return (
    <StudentCtx.Provider value={{ me, memberships, activeTid, activeMem, theme, refreshMe, switchInstitution, setMe, customStickers }}>
      <div
        className="min-h-screen w-full"
        style={{
          ...cssVars,
          background: 'var(--st-bg)',
          color: 'var(--st-ink)',
          fontFamily: 'var(--st-font-body)',
          fontSize: `calc(1rem * ${fontScale})`,
          // Native controls, scrollbars and form autofill follow the theme.
          colorScheme: theme.dark ? 'dark' : 'light',
        }}
        data-testid="student-shell"
        data-theme-id={themeId}
        data-theme-tier={theme.tier}
        data-theme-mode={theme.dark ? 'dark' : 'light'}>
        <style>{`
          [data-testid="student-shell"] h1,
          [data-testid="student-shell"] h2,
          [data-testid="student-shell"] h3,
          [data-testid="student-shell"] h4 {
            font-family: var(--st-font-heading) !important;
            letter-spacing: var(--st-letter-spacing, 0);
            font-weight: var(--st-heading-weight, 600);
          }
          /* Every theme group keeps its own card and pill shape. */
          [data-testid="student-shell"] :is(.rounded-2xl, .rounded-3xl):not([data-exam-mode-panel] *) {
            border-radius: var(--st-radius-card);
          }
          /* Bordered page cards sit a little off the page; clickable ones pick
             up the theme colour on hover. Inline shadows (selected states) win. */
          [data-testid="student-main"] :is(.rounded-2xl, .rounded-3xl):is(.border, .border-2):not(.border-dashed):not([class*="shadow-"]):not([data-exam-mode-panel] *) {
            box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
          }
          [data-testid="student-main"] :is(a, button):is(.rounded-2xl, .rounded-3xl):is(.border, .border-2):not(:disabled):not([data-exam-mode-panel] *):hover {
            border-color: color-mix(in srgb, var(--st-primary) 45%, var(--st-border)) !important;
            box-shadow: 0 4px 16px -6px color-mix(in srgb, var(--st-primary) 35%, transparent);
          }
          /* Dark-theme adapter — hardcoded light Tailwind colours (bg-white,
             text-gray-900, border-gray-200, …) remap to the theme's own
             palette on dark themes only; light themes are untouched. Inside the
             Exam Mode chat panel the same rules resolve to its fixed dark
             palette (its --st-* vars), since Exam Mode follows the theme's mode. */
          [data-theme-mode="dark"] .bg-white {
            background-color: var(--st-card) !important;
          }
          [data-theme-mode="dark"] :is(.bg-gray-50, .bg-gray-100, .bg-slate-50, .bg-slate-100, .bg-zinc-50, .bg-zinc-100, .bg-neutral-50, .bg-neutral-100) {
            background-color: color-mix(in srgb, var(--st-card) 88%, var(--st-ink)) !important;
          }
          [data-theme-mode="dark"] :is(.text-gray-900, .text-gray-800, .text-gray-700, .text-slate-900, .text-slate-800, .text-slate-700, .text-zinc-900, .text-zinc-800, .text-zinc-700, .text-neutral-900, .text-neutral-800, .text-black) {
            color: var(--st-ink) !important;
          }
          [data-theme-mode="dark"] :is(.text-gray-600, .text-gray-500, .text-slate-600, .text-slate-500, .text-zinc-600, .text-zinc-500, .text-neutral-600, .text-neutral-500) {
            color: var(--st-mute) !important;
          }
          [data-theme-mode="dark"] :is(.border-gray-100, .border-gray-200, .border-gray-300, .border-slate-100, .border-slate-200, .border-slate-300, .border-zinc-100, .border-zinc-200, .border-neutral-200) {
            border-color: var(--st-border) !important;
          }
          /* Per-tier shape adapter — buttons & chips reshape to the active vibe.
             We re-target only buttons & links so circular avatars/swatches stay round. */
          [data-theme-tier="professional"] button.rounded-full,
          [data-theme-tier="professional"] a.rounded-full,
          [data-theme-tier="professional"] span.rounded-full {
            border-radius: var(--st-radius-pill) !important;
          }
          [data-theme-tier="professional"] .rounded-3xl,
          [data-theme-tier="professional"] .rounded-2xl {
            border-radius: var(--st-radius-card) !important;
          }
          /* Rules below with HARDCODED values (not var()-driven) skip the
             tutor chat panel while Exam Mode is on ([data-exam-mode-panel]):
             Exam Mode pins one look on every theme, and a CSS-variable
             override on the panel can't neutralise a fixed font-size. */
          [data-theme-tier="professional"] button:not([data-exam-mode-panel] *),
          [data-theme-tier="professional"] a[role="button"]:not([data-exam-mode-panel] *) {
            letter-spacing: 0.02em;
          }
          /* Professional pro-reading typography — smaller refined body size,
             generous line-height, capped reading width, tabular numerals. */
          [data-theme-tier="professional"] {
            font-size: var(--st-body-size, 0.9375rem);
            line-height: var(--st-line-height, 1.7);
            font-feature-settings: "kern", "liga", "calt", "tnum";
            font-variant-numeric: tabular-nums;
          }
          [data-theme-tier="professional"] p,
          [data-theme-tier="professional"] li {
            max-width: var(--st-reading-max, 66ch);
            line-height: var(--st-line-height, 1.7);
          }
          [data-theme-tier="professional"] h1:not([data-exam-mode-panel] *) { font-size: clamp(2rem, 2.4vw + 1rem, 2.8rem); line-height: 1.15; letter-spacing: -0.01em; }
          [data-theme-tier="professional"] h2:not([data-exam-mode-panel] *) { font-size: clamp(1.4rem, 1.2vw + 0.9rem, 1.85rem); line-height: 1.25; }
          [data-theme-tier="professional"] h3:not([data-exam-mode-panel] *) { font-size: 1.2rem; line-height: 1.35; }
          [data-theme-tier="professional"] .text-xs:not([data-exam-mode-panel] *)  { font-size: 0.75rem; letter-spacing: 0.015em; }
          [data-theme-tier="professional"] .text-sm:not([data-exam-mode-panel] *)  { font-size: 0.8125rem; }
          [data-theme-tier="professional"] .text-base:not([data-exam-mode-panel] *) { font-size: 0.9375rem; }
          [data-theme-tier="heroic"] button.rounded-full,
          [data-theme-tier="heroic"] a.rounded-full,
          [data-theme-tier="heroic"] span.rounded-full {
            border-radius: var(--st-radius-pill) !important;
          }
          /* Heroic and sports themes: capitals on the page title only. */
          :is([data-theme-tier="heroic"], [data-theme-tier="sports"]) h1:not([data-exam-mode-panel] *) {
            text-transform: var(--st-heading-transform, none);
            letter-spacing: 0.02em;
          }
          [data-theme-tier="playful"] .rounded-2xl { border-radius: var(--st-radius-card) !important; }
        `}</style>

        {/* Single sticker layer for the whole student app: Settings-picked
            stickers AND drag-placed ones. They used to live in two layers —
            a non-interactive background one for Settings stickers and this
            board for placed ones — which is why Customize could never edit a
            Settings sticker. The board now owns both; the Customize button
            itself still only appears on the Desk (see isCustomizableRoute). */}
        <PlacedStickerBoard
          initial={profile.placed_stickers || []}
          settingsStickerIds={profile.stickers || []}
          adoptedStickerIds={profile.adopted_sticker_ids || []}
          customStickers={customStickers}
          onSaved={refreshMe}
          dark={theme.dark}
          editable={isCustomizableRoute(location.pathname)}
        />

        {/* Global Trophy Claim watcher — auto-pops the address modal the
            moment a 100/500/1000/5000-badge milestone is reached and
            renders a persistent notification bar until the student claims. */}
        <TrophyClaimGate />

        {/* Header */}
        <header className="sticky top-0 z-40 backdrop-blur border-b" style={{ background: 'color-mix(in srgb, var(--st-card) 88%, transparent)', borderColor: 'var(--st-border)' }}>
          <div className="w-full px-4 sm:px-6 h-16 flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden p-2 rounded-lg hover:bg-black/5" data-testid="sidebar-toggle">
              <Menu className="w-5 h-5" />
            </button>
            <button onClick={() => nav('/student/desk')} className="flex items-center gap-2" data-testid="header-logo">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center font-semibold text-sm" style={{ background: 'var(--st-primary)', color: 'var(--st-on-primary)' }}>D</div>
              <span className="text-base font-semibold" style={{ fontFamily: 'var(--st-font-heading)' }}>MyDeskey</span>
            </button>

            {/* Institution Switcher */}
            <div className="relative ml-3">
              <button onClick={() => setShowSwitcher(s => !s)}
                className="flex items-center gap-2 h-9 pl-1.5 pr-2.5 rounded-lg text-sm border transition-colors hover:bg-[color-mix(in_srgb,var(--st-ink)_4%,var(--st-card))]"
                style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
                data-testid="switch-institution-btn">
                <span className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-semibold" style={{ background: 'var(--st-primary)', color: 'var(--st-on-primary)' }}>
                  {(activeMem?.tenant_name || 'My').slice(0, 2).toUpperCase()}
                </span>
                <span className="font-medium max-w-[160px] truncate">{activeMem?.tenant_name || 'No institution yet'}</span>
                <ChevronDown className="w-3.5 h-3.5 opacity-60" />
              </button>
              {showSwitcher && (
                <SwitchPopover memberships={memberships} activeTid={activeTid} onSwitch={switchInstitution} onClose={() => setShowSwitcher(false)} />
              )}
            </div>

            <div className="flex-1" />

            {/* Badges dropdown */}
            <BadgesDropdown refreshTick={badgeRefresh} />

            {/* Assignments "X pending" header pill — only renders when there
                is something due. Click → /student/assignments. */}
            <AssignmentsHeaderPill />

            {/* Physical trophy "X ready" pill — only renders when student
                has an unclaimed plaque waiting for a shipping address. */}
            <TrophyClaimHeaderPill />

            {/* Credits chip */}
            <button onClick={() => nav('/student/credits')} className="hidden sm:flex items-center gap-2 h-9 px-3 rounded-lg border text-sm font-medium tabular-nums transition-colors hover:bg-[color-mix(in_srgb,var(--st-ink)_4%,var(--st-card))]" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid="credits-chip" title="School credits · Super credits">
              <Wallet className="w-4 h-4" style={{ color: 'var(--st-mute)' }} />
              <span>{activeMem?.credits_remaining || 0}</span>
              <span style={{ color: 'var(--st-mute)' }}>·</span>
              <span className="inline-flex items-center gap-1">
                <Zap className="w-4 h-4" style={{ color: 'var(--st-primary-ink)' }} />
                {me.super_credits || 0}
              </span>
            </button>

            {/* Avatar — click opens a popover with name/email/badge + logout */}
            <StudentAvatarMenu me={me} onLogout={signOut} />
          </div>
        </header>

        {/* Body: Sidebar + Content */}
        <div className="flex w-full">
          {/* Desktop sidebar — hidden in immersive (LearnNow) routes */}
          {!isImmersive && (
            <aside className="hidden md:block w-60 flex-shrink-0 sticky top-16 h-[calc(100vh-64px)] overflow-y-auto py-4 px-3 border-r" style={{ borderColor: 'var(--st-border)' }}>
              <StudentSidebar />
            </aside>
          )}

          {/* Mobile sidebar drawer */}
          {sidebarOpen && (
            <div className="md:hidden fixed inset-0 z-50">
              <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
              <aside className="absolute top-0 left-0 bottom-0 w-72 p-5" style={{ background: 'var(--st-card)' }}>
                <button onClick={() => setSidebarOpen(false)} className="absolute top-3 right-3 p-2"><X className="w-5 h-5" /></button>
                <div className="mt-8" onClick={() => setSidebarOpen(false)}><StudentSidebar /></div>
              </aside>
            </div>
          )}

          {/* Content */}
          <main className={`flex-1 min-w-0 relative z-10 ${isImmersive ? 'p-0' : 'px-4 sm:px-8 py-6 sm:py-8 max-w-[1600px]'}`} data-testid="student-main">
            <Routes>
              <Route index element={<Navigate to="desk" replace />} />
              <Route path="desk" element={<StudentDeskPage />} />
              <Route path="mydesk" element={<MyDeskPage />} />
              <Route path="mydesk/class/:classId" element={<MyDeskClassPage />} />
              <Route path="mydesk/lesson/:lessonId" element={<LearnNowPage />} />
              {/* /student/institution legacy → unified Desk */}
              <Route path="institution" element={<Navigate to="/student/desk" replace />} />
              {/* Legacy class/topic pages — redirect to the rich MyDesk routes
                  so every entry point (Student Desk → My Classes, Recently
                  Published cards, Recommendations, Assignments) renders with
                  the new `LessonView` instead of the old basic page. */}
              <Route path="class/:classId" element={<LegacyClassRedirect />} />
              <Route path="topic/:topicId" element={<LegacyTopicRedirect />} />
              <Route path="hub" element={<StudentHubPage />} />
              <Route path="exam-prep" element={<ExamPrepHub />} />
              <Route path="exam-prep/formula-sheet" element={<FormulaSheetPage />} />
              <Route path="exam-prep/practice" element={<PracticePyqPage />} />
              <Route path="quizzes" element={<MyQuizzesPage />} />
              <Route path="quizzes/create" element={<CreateQuizPage />} />
              <Route path="quizzes/:quizId" element={<StudentQuizAttempt />} />
              <Route path="analytics" element={<StudentAnalyticsPage />} />
              <Route path="badges" element={<StudentBadgesPage />} />
              <Route path="assignments" element={<StudentAssignmentsPage />} />
              <Route path="settings" element={<StudentSettingsPage />} />
              <Route path="credits" element={<StudentCreditsPage />} />
              <Route path="*" element={<Navigate to="desk" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </StudentCtx.Provider>
  );
}

// ─── Switch Popover ────────────────────────────────────────────────────────
function SwitchPopover({ memberships, activeTid, onSwitch, onClose }) {
  const nav = useNavigate();
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute top-full mt-2 left-0 z-50 w-80 max-h-[400px] overflow-auto rounded-xl shadow-lg border p-2" style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)' }} data-testid="switch-popover">
        <div className="px-3 py-2 text-sm" style={{ color: 'var(--st-mute)' }}>Your schools <span className="tabular-nums">{memberships.length}</span></div>
        {memberships.length === 0 && (
          <div className="p-4 text-center">
            <p className="text-sm mb-1">You haven't joined a school yet</p>
            <p className="text-sm" style={{ color: 'var(--st-mute)' }}>Ask your school admin to invite the email you signed up with</p>
          </div>
        )}
        {memberships.map(m => (
          <button key={m.tenant_id}
            data-testid={`switch-tenant-${m.tenant_id}`}
            onClick={() => onSwitch(m.tenant_id)}
            className="w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors hover:bg-[color-mix(in_srgb,var(--st-ink)_5%,transparent)]"
            style={m.tenant_id === activeTid ? { background: 'color-mix(in srgb, var(--st-primary) 10%, var(--st-card))' } : undefined}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-semibold flex-shrink-0" style={{ background: 'var(--st-primary)', color: 'var(--st-on-primary)' }}>
              {(m.tenant_name || '??').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{m.tenant_name}</div>
              <div className="text-xs truncate capitalize" style={{ color: 'var(--st-mute)' }}>{m.department_name || m.tenant_type || '—'} · {m.role}</div>
            </div>
            {m.tenant_id === activeTid && <span className="text-xs font-medium" style={{ color: 'var(--st-primary-ink)' }}>Current</span>}
          </button>
        ))}
        <button onClick={() => { onClose(); nav('/student/desk'); }} className="w-full mt-2 h-9 px-3 text-sm font-medium rounded-lg border transition-colors hover:bg-[color-mix(in_srgb,var(--st-ink)_5%,transparent)]" style={{ borderColor: 'var(--st-border)' }}>
          Manage institutions
        </button>
      </div>
    </>
  );
}


